import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadError, errorMessage } from '../components/LoadError';
import { StatusBadge } from '../components/StatusBadge';
import { TablePager } from '../components/TableControls';
import { SkuInput } from '../components/purchases/SkuInput';
import { useDebouncedValue } from '../components/sales/useAbortableGet';
import {
  RotateCcw, PackageCheck, Wrench, Trash2, ClipboardCheck,
  BarChart3, Search, Plus, Download, Loader2, X,
  AlertTriangle, TrendingDown, CheckCircle2, Clock,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, LineChart, Line } from 'recharts';
import { platformLabel } from '../config/platforms';

import { useChannels } from '../hooks/useChannels';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHART_COLORS = ['#007AFF', '#FF9500', '#FF2D55', '#AF52DE', '#34C759', '#5856D6', '#FF3B30', '#00C7BE'];

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { key: 'entry', label: 'Return Entry', icon: Plus },
  { key: 'qc', label: 'QC Inspection', icon: ClipboardCheck },
  { key: 'damage', label: 'Damage Store', icon: Trash2 },
  { key: 'spare', label: 'Spare Dept', icon: Wrench },
];

const GRADE_CONFIG = {
  A: { label: 'Sellable', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', icon: PackageCheck },
  B: { label: 'Repairable', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: Wrench },
  C: { label: 'Damaged', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: Trash2 },
};

// Return statuses on the shared StatusBadge (GRADED is not in its default map).
const RETURN_STATUS_TONE = {
  RECEIVED: 'info',
  QC_PENDING: 'warning',
  GRADED: 'info',
  PROCESSED: 'success',
  CLOSED: 'neutral',
};

export const ReturnsPage = () => {
  useChannels();   // re-render when channel names or colours change (Settings → Channels)
  const { country, currency } = useCountry();
  const [tab, setTab] = useState('dashboard');
  const headers = useMemo(() => getAuthHeaders(), []);

  return (
    <div className="max-w-[1600px] mx-auto space-y-6" data-testid="returns-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Return Tracking</h1>
          <p className="mt-1 text-[13px] text-gray-500">Grade, route and track every returned product</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex max-w-full items-center overflow-x-auto bg-white border border-gray-200 rounded-xl p-1 shadow-sm w-fit" role="tablist" aria-label="Return tracking sections" data-testid="returns-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`flex shrink-0 items-center gap-1.5 px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg text-[12px] font-medium transition-all duration-200 whitespace-nowrap ${
              tab === key ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
            data-testid={`tab-${key}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'dashboard' && <DashboardTab country={country} currency={currency} headers={headers} />}
      {tab === 'entry' && <EntryTab country={country} currency={currency} headers={headers} />}
      {tab === 'qc' && <QCTab country={country} currency={currency} headers={headers} />}
      {tab === 'damage' && <DamageTab country={country} currency={currency} headers={headers} />}
      {tab === 'spare' && <SpareTab country={country} currency={currency} headers={headers} />}
    </div>
  );
};

// ═══════════════════════════════════════
// DASHBOARD TAB
// ═══════════════════════════════════════
const DashboardTab = ({ country, currency, headers }) => {
  const { data, loading, error, reload } = useTabData(`${API}/returns/dashboard?country=${country}`, headers);

  if (loading) return <LoadingState />;
  if (error || !data) return <LoadError what="the returns dashboard" error={error} onRetry={reload} testId="returns-dashboard-error" />;

  const kpis = [
    { label: 'Returns Today', value: data.total_today, icon: RotateCcw, accent: 'border-blue-200', iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
    { label: 'QC Pending', value: data.qc_pending, icon: Clock, accent: 'border-amber-200', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
    { label: 'Sellable (30d)', value: data.sellable, icon: PackageCheck, accent: 'border-green-200', iconBg: 'bg-green-100', iconColor: 'text-green-600' },
    { label: 'Repairable (30d)', value: data.repairable, icon: Wrench, accent: 'border-orange-200', iconBg: 'bg-orange-100', iconColor: 'text-orange-600' },
    { label: 'Damaged (30d)', value: data.damaged, icon: Trash2, accent: 'border-red-200', iconBg: 'bg-red-100', iconColor: 'text-red-600' },
    { label: 'Return Rate', value: `${data.return_rate}%`, icon: TrendingDown, accent: 'border-purple-200', iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
  ];

  const platformData = Object.entries(data.by_platform || {}).map(([p, c]) => ({ platform: platformLabel(p), count: c }));
  const reasonData = (data.by_reason || []).slice(0, 6);

  return (
    <div className="space-y-6" data-testid="returns-dashboard">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map(({ label, value, icon: Icon, accent, iconBg, iconColor }) => (
          <Card key={label} className={`p-5 bg-white rounded-2xl border ${accent} shadow-sm`} data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">{label}</p>
                <p className="text-3xl font-bold tracking-tighter text-gray-900 mt-1">{value}</p>
              </div>
              <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center`}>
                <Icon className={`h-4 w-4 ${iconColor}`} strokeWidth={2} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Revenue impact */}
      {data.total_damage_loss > 0 && (
        <Card className="p-5 bg-red-50 border border-red-200 rounded-2xl shadow-sm" data-testid="damage-loss-card">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <div>
              <p className="text-[13px] font-semibold text-red-800">Estimated Revenue Loss from Damaged Returns</p>
              <p className="text-2xl font-bold text-red-700 mt-0.5">{currency} {data.total_damage_loss.toLocaleString('en-IN')}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily trend */}
        <Card className="lg:col-span-2 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Returns Trend (14 Days)</h3>
          {data.daily_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="date" fontSize={10} tickLine={false} axisLine={false} tickFormatter={v => v.slice(5)} />
                <YAxis fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ border: 'none', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,.08)' }} />
                <Line type="monotone" dataKey="count" stroke="#FF2D55" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </Card>

        {/* By platform pie */}
        <Card className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Returns by Platform</h3>
          {platformData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={platformData} dataKey="count" nameKey="platform" cx="50%" cy="50%" outerRadius={70} innerRadius={40}
                  label={({ platform, percent }) => `${platform} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {platformData.map((entry, i) => <Cell key={entry.platform} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </Card>
      </div>

      {/* Reason chart + High return SKUs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Top Return Reasons</h3>
          {reasonData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={reasonData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="reason" fontSize={10} tickLine={false} axisLine={false} width={120} />
                <Tooltip contentStyle={{ border: 'none', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,.08)' }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {reasonData.map((entry, i) => <Cell key={entry.reason} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </Card>

        <Card className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4">High Return Rate SKUs</h3>
          <div className="space-y-2 max-h-[220px] overflow-y-auto">
            {data.high_return_skus?.length > 0 ? data.high_return_skus.map((s, i) => (
              <div key={s.sku_code} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl" data-testid={`high-return-sku-${i}`}>
                <div className="min-w-0">
                  <p className="text-[12px] font-medium text-gray-900 truncate">{s.product_name || s.sku_code}</p>
                  <p className="text-[10px] font-mono text-gray-500">{s.sku_code}</p>
                </div>
                <span className="text-[12px] font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded-full">{s.count}</span>
              </div>
            )) : <p className="text-[13px] text-gray-500 text-center py-8">No return data yet</p>}
          </div>
        </Card>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// RETURN ENTRY TAB
// ═══════════════════════════════════════
const PAGE_SIZE = 50;

// Today's date in India, so returns logged before 05:30 IST don't default to yesterday (UTC).
const todayIST = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
const emptyForm = () => ({ order_id: '', sku_code: '', platform: 'AMAZON_RETAIL', return_reason: 'Defective Product', return_date: todayIST(), quantity: '1', qc_notes: '' });

const EntryTab = ({ country, currency, headers }) => {
  const [returns, setReturns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [meta, setMeta] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 350);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Static dropdown lists: fetch once.
  useEffect(() => {
    axios.get(`${API}/returns/meta`, { headers })
      .then(res => setMeta(res.data))
      .catch(() => toast.error('Could not load platforms and reasons'));
  }, [headers]);

  // Back to page 1 whenever the filters change.
  useEffect(() => { setPage(1); }, [debouncedSearch, filterPlatform, filterStatus, country]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ country, with_total: 'true', skip: String((page - 1) * PAGE_SIZE), limit: String(PAGE_SIZE) });
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filterPlatform) params.set('platform', filterPlatform);
    if (filterStatus) params.set('status', filterStatus);
    axios.get(`${API}/returns?${params}`, { headers, signal: controller.signal })
      .then(res => {
        const data = res.data;
        // Tolerate an older backend that still returns a plain list.
        setReturns(Array.isArray(data) ? data : data.rows || []);
        setTotal(Array.isArray(data) ? data.length : data.total || 0);
      })
      .catch(err => { if (!controller.signal.aborted) setError(err); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [country, debouncedSearch, filterPlatform, filterStatus, page, headers, reloadKey]);

  const reload = () => setReloadKey(k => k + 1);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pager = { total, pageSize: PAGE_SIZE, page, pageCount, setPage };

  const validate = () => {
    const errs = {};
    if (!form.order_id.trim()) errs.order_id = 'Order ID is required';
    if (!form.sku_code.trim()) errs.sku_code = 'SKU code is required';
    if (!form.return_date) errs.return_date = 'Return date is required';
    const qty = Number(form.quantity);
    if (form.quantity === '' || !Number.isInteger(qty) || qty < 1) errs.quantity = 'Enter a whole number of 1 or more';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async () => {
    if (saving || !validate()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/returns`, { ...form, order_id: form.order_id.trim(), sku_code: form.sku_code.trim(), quantity: Number(form.quantity), country }, { headers });
      toast.success('Return logged');
      setShowForm(false);
      setForm(emptyForm());
      setFormErrors({});
      setPage(1);
      reload();
    } catch (e) {
      const msg = errorMessage(e, 'Failed to log the return');
      if (/not found in SKU Master/i.test(msg)) setFormErrors(prev => ({ ...prev, sku_code: msg }));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/returns/export?country=${country}`, { headers });
      const link = document.createElement('a');
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${res.data.file}`;
      link.download = res.data.filename;
      link.click();
      toast.success('Exported');
    } catch (e) { toast.error(errorMessage(e, 'Export failed')); }
  };

  const fieldLabel = 'text-[11px] font-semibold uppercase tracking-wider text-gray-600 block mb-1';
  const fieldError = (key) => formErrors[key] && <p className="mt-1 text-[11px] text-red-700" role="alert">{formErrors[key]}</p>;

  return (
    <div className="space-y-4" data-testid="returns-entry">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" aria-hidden="true" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SKU, Order, Return ID..." aria-label="Search returns" className="pl-9 h-9 text-[13px] rounded-lg border-gray-200" data-testid="search-input" />
        </div>
        <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} aria-label="Platform" className="h-9 px-3 text-[12px] border border-gray-200 rounded-lg bg-white text-gray-700" data-testid="filter-platform">
          <option value="">All Platforms</option>
          {(meta?.platforms || []).map(p => <option key={p} value={p}>{platformLabel(p)}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} aria-label="Status" className="h-9 px-3 text-[12px] border border-gray-200 rounded-lg bg-white text-gray-700" data-testid="filter-status">
          <option value="">All Status</option>
          {(meta?.statuses || []).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExport} className="h-9 text-[12px] border-gray-200 rounded-lg" data-testid="export-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export
          </Button>
          <Button onClick={() => { setForm(emptyForm()); setFormErrors({}); setShowForm(true); }} className="h-9 px-4 bg-gray-900 text-white text-[12px] font-medium rounded-lg" data-testid="new-return-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Log Return
          </Button>
        </div>
      </div>

      {/* New Return Form */}
      {showForm && (
        <Card className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="return-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">Log New Return</h3>
            <Button variant="ghost" size="icon" onClick={() => setShowForm(false)} className="h-7 w-7" aria-label="Close form" data-testid="close-return-form"><X className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="return-order-id" className={fieldLabel}>Order ID *</label>
              <Input id="return-order-id" value={form.order_id} onChange={e => setForm(f => ({ ...f, order_id: e.target.value }))} aria-invalid={!!formErrors.order_id} className="h-9 text-[13px] rounded-lg" data-testid="form-order-id" />
              {fieldError('order_id')}
            </div>
            <div
              role="group"
              aria-labelledby="return-sku-label"
              aria-describedby={formErrors.sku_code ? 'return-sku-help return-sku-error' : 'return-sku-help'}
              aria-invalid={!!formErrors.sku_code}
              data-testid="form-sku"
            >
              {/* SkuInput takes no id, so the label wraps it to name its input. */}
              <label className="block">
                <span id="return-sku-label" className={fieldLabel}>SKU Code *</span>
                <SkuInput
                  value={form.sku_code}
                  country={country}
                  index="return"
                  onPick={({ sku_code }) => { setForm(f => ({ ...f, sku_code })); setFormErrors(prev => ({ ...prev, sku_code: undefined })); }}
                />
              </label>
              <p id="return-sku-help" className="mt-1 text-[11px] text-gray-500">Type 2+ characters to pick from the SKU master.</p>
              {formErrors.sku_code && <p id="return-sku-error" className="mt-1 text-[11px] text-red-700" role="alert">{formErrors.sku_code}</p>}
            </div>
            <div>
              <label htmlFor="return-platform" className={fieldLabel}>Platform</label>
              <select id="return-platform" value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))} className="w-full h-9 px-3 text-[13px] border border-gray-200 rounded-lg bg-white" data-testid="form-platform">
                {(meta?.platforms || [form.platform]).map(p => <option key={p} value={p}>{platformLabel(p)}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="return-date" className={fieldLabel}>Return Date</label>
              <Input id="return-date" type="date" value={form.return_date} onChange={e => setForm(f => ({ ...f, return_date: e.target.value }))} aria-invalid={!!formErrors.return_date} className="h-9 text-[13px] rounded-lg" data-testid="form-date" />
              {fieldError('return_date')}
            </div>
            <div>
              <label htmlFor="return-reason" className={fieldLabel}>Return Reason</label>
              <select id="return-reason" value={form.return_reason} onChange={e => setForm(f => ({ ...f, return_reason: e.target.value }))} className="w-full h-9 px-3 text-[13px] border border-gray-200 rounded-lg bg-white" data-testid="form-reason">
                {(meta?.return_reasons || [form.return_reason]).map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="return-qty" className={fieldLabel}>Quantity (units)</label>
              <Input id="return-qty" type="number" min={1} step={1} inputMode="numeric" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} aria-invalid={!!formErrors.quantity} className="h-9 text-[13px] rounded-lg" data-testid="form-qty" />
              {fieldError('quantity')}
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="return-notes" className={fieldLabel}>QC Notes</label>
              <Input id="return-notes" value={form.qc_notes} onChange={e => setForm(f => ({ ...f, qc_notes: e.target.value }))} placeholder="Initial notes..." className="h-9 text-[13px] rounded-lg" data-testid="form-notes" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={handleCreate} disabled={saving} className="h-9 px-5 bg-gray-900 text-white text-[12px] font-medium rounded-lg" data-testid="submit-return-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
              {saving ? 'Saving…' : 'Submit Return'}
            </Button>
          </div>
        </Card>
      )}

      {/* Returns Table */}
      {error ? (
        <LoadError what="returns" error={error} onRetry={reload} retrying={loading} testId="returns-load-error" />
      ) : (
        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto relative" aria-busy={loading}>
            <table className="w-full text-[12px]" data-testid="returns-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Return ID', 'Order ID', 'SKU', 'Product', 'Platform', 'Reason', 'Date', 'Qty', 'Grade', 'Status'].map(h => (
                    <th key={h} className="text-left py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-[10px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={loading ? 'opacity-50' : ''}>
                {returns.length > 0 ? returns.map(r => (
                  <tr key={r.return_id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`return-row-${r.return_id}`}>
                    <td className="py-2.5 px-4 font-mono text-[11px] text-blue-700">{r.return_id}</td>
                    <td className="py-2.5 px-4 font-mono text-gray-600">{r.order_id}</td>
                    <td className="py-2.5 px-4 font-mono text-gray-600">{r.sku_code}</td>
                    <td className="py-2.5 px-4 text-gray-900 max-w-[160px] truncate">{r.product_name}</td>
                    <td className="py-2.5 px-4"><span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md font-medium whitespace-nowrap">{r.platform ? platformLabel(r.platform) : ''}</span></td>
                    <td className="py-2.5 px-4 text-gray-600 max-w-[120px] truncate">{r.return_reason}</td>
                    <td className="py-2.5 px-4 text-gray-600 whitespace-nowrap">{r.return_date}</td>
                    <td className="py-2.5 px-4 text-center font-medium">{r.quantity}</td>
                    <td className="py-2.5 px-4">
                      {r.grade ? (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${GRADE_CONFIG[r.grade]?.bg} ${GRADE_CONFIG[r.grade]?.color}`}>
                          {GRADE_CONFIG[r.grade]?.label}
                        </span>
                      ) : <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusBadge status={r.status} tone={RETURN_STATUS_TONE[r.status]} />
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={10} className="py-12 text-center text-gray-500 text-[13px]">
                    {loading ? 'Loading returns…' : (debouncedSearch || filterPlatform || filterStatus ? 'No returns match your filters.' : 'No returns found. Click "Log Return" to add one.')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePager table={pager} itemLabel="returns" className="border-t border-gray-100" />
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════
// QC INSPECTION TAB
// ═══════════════════════════════════════
const QCTab = ({ country, currency, headers }) => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [grading, setGrading] = useState(null); // return_id being inspected
  const [notesById, setNotesById] = useState({}); // QC notes kept per return
  const [cardErrors, setCardErrors] = useState({}); // last server error per return
  const [confirm, setConfirm] = useState(null); // { ret, grade } awaiting confirmation
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    axios.get(`${API}/returns?country=${country}&status=QC_PENDING`, { headers })
      .then(res => { if (alive) setPending(Array.isArray(res.data) ? res.data : res.data.rows || []); })
      .catch(err => { if (alive) setError(err); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [country, headers, reloadKey]);

  const notesFor = (id) => notesById[id] || '';

  const askGrade = (ret, grade) => {
    if ((grade === 'B' || grade === 'C') && !notesFor(ret.return_id).trim()) {
      setCardErrors(prev => ({ ...prev, [ret.return_id]: 'Inspection remarks are required for Repairable and Damaged grades.' }));
      return;
    }
    setCardErrors(prev => ({ ...prev, [ret.return_id]: undefined }));
    setConfirm({ ret, grade });
  };

  const submitGrade = async () => {
    const { ret, grade } = confirm;
    const returnId = ret.return_id;
    setSavingId(returnId);
    try {
      await axios.put(`${API}/returns/${returnId}/grade?country=${country}`, { grade, qc_notes: notesFor(returnId) }, { headers });
      toast.success(`${returnId} graded ${GRADES[grade]?.label}, routed to ${GRADE_ROUTES[grade]}`);
      setPending(prev => prev.filter(r => r.return_id !== returnId));
      setGrading(null);
      setNotesById(prev => { const next = { ...prev }; delete next[returnId]; return next; });
    } catch (e) {
      const msg = errorMessage(e, 'Grading failed');
      setCardErrors(prev => ({ ...prev, [returnId]: msg }));
      toast.error(msg);
      if (e.response?.status === 409 && !e.response?.data?.detail?.code) {
        // Already graded elsewhere (or not gradable here): it no longer belongs in this queue.
        // A business-rule 409 (detail.code, e.g. OVER_RETURN) keeps the card so B or C can still be chosen.
        setPending(prev => prev.filter(r => r.return_id !== returnId));
        setGrading(null);
        setConfirm(null);
        return; // close the dialog; nothing left to retry
      }
      throw e; // keep the confirm dialog open so the user can retry or cancel
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <LoadError what="the QC queue" error={error} onRetry={() => setReloadKey(k => k + 1)} testId="qc-load-error" />;

  const confirmGrade = confirm?.grade;
  const confirmQty = confirm?.ret?.quantity;

  return (
    <div className="space-y-4" data-testid="qc-inspection">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-gray-600">{pending.length} items pending quality check</p>
      </div>

      {pending.length === 0 ? (
        <Card className="p-12 bg-white rounded-2xl border border-gray-100 shadow-sm text-center" data-testid="qc-empty">
          <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-3" />
          <p className="text-[15px] font-medium text-gray-700">All caught up!</p>
          <p className="text-[13px] text-gray-500 mt-1">No returns pending QC inspection</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="qc-list">
          {pending.map(r => {
            const isActive = grading === r.return_id;
            const notes = notesFor(r.return_id);
            const busy = savingId === r.return_id;
            const cardError = cardErrors[r.return_id];
            return (
              <Card key={r.return_id} className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${isActive ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-100'}`} data-testid={`qc-card-${r.return_id}`}>
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-gray-900">{r.product_name}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                        <span className="text-[10px] font-mono text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded">{r.sku_code}</span>
                        <span className="text-[10px] font-mono text-gray-600">{r.return_id}</span>
                      </div>
                    </div>
                    <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md font-medium whitespace-nowrap">{r.platform ? platformLabel(r.platform) : ''}</span>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-1 mb-3 text-[11px]">
                    <div><span className="text-gray-500">Order:</span> <span className="font-medium text-gray-700 break-all">{r.order_id}</span></div>
                    <div><span className="text-gray-500">Qty:</span> <span className="font-medium text-gray-700">{r.quantity}</span></div>
                    <div><span className="text-gray-500">Date:</span> <span className="font-medium text-gray-700">{r.return_date}</span></div>
                  </div>
                  <div className="text-[11px] mb-3">
                    <span className="text-gray-500">Reason:</span> <span className="font-medium text-gray-700">{r.return_reason}</span>
                  </div>

                  {/* QC Notes input (when active) — kept per return, never shared between cards */}
                  {isActive && (
                    <div className="mb-3">
                      <label htmlFor={`qc-notes-${r.return_id}`} className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 block mb-1">
                        QC Notes <span className="text-red-700 normal-case">* required for Repairable / Damaged</span>
                      </label>
                      <Input
                        id={`qc-notes-${r.return_id}`}
                        value={notes}
                        onChange={e => { const v = e.target.value; setNotesById(prev => ({ ...prev, [r.return_id]: v })); }}
                        placeholder="Inspection remarks..."
                        disabled={busy}
                        className={`h-9 text-[12px] rounded-lg ${!notes.trim() ? 'border-amber-300 focus:border-amber-400' : ''}`}
                        data-testid="qc-notes-input"
                      />
                    </div>
                  )}

                  {cardError && (
                    <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800" role="alert" data-testid={`qc-error-${r.return_id}`}>
                      {cardError}
                    </p>
                  )}

                  {/* Grade buttons: stacked on phones, 44px tall, each asks for confirmation */}
                  {isActive ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Button onClick={() => askGrade(r, 'A')} disabled={busy} className="h-11 sm:h-9 bg-green-700 hover:bg-green-800 text-white text-[12px] sm:text-[11px] font-semibold rounded-lg" data-testid="grade-a-btn">
                        <PackageCheck className="h-3.5 w-3.5 mr-1" /> A · Sellable
                      </Button>
                      <Button onClick={() => askGrade(r, 'B')} disabled={busy} className="h-11 sm:h-9 bg-amber-600 hover:bg-amber-700 text-white text-[12px] sm:text-[11px] font-semibold rounded-lg" data-testid="grade-b-btn">
                        <Wrench className="h-3.5 w-3.5 mr-1" /> B · Repairable
                      </Button>
                      <Button onClick={() => askGrade(r, 'C')} disabled={busy} className="h-11 sm:h-9 bg-red-700 hover:bg-red-800 text-white text-[12px] sm:text-[11px] font-semibold rounded-lg" data-testid="grade-c-btn">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> C · Damaged
                      </Button>
                      <Button variant="outline" disabled={busy} onClick={() => { setGrading(null); setCardErrors(prev => ({ ...prev, [r.return_id]: undefined })); }} className="h-11 sm:h-9 sm:col-span-3 text-[12px] sm:text-[11px] rounded-lg border-gray-200" data-testid="cancel-grade-btn">Cancel</Button>
                    </div>
                  ) : (
                    <Button onClick={() => setGrading(r.return_id)} className="w-full h-11 sm:h-9 bg-gray-900 text-white text-[12px] sm:text-[11px] font-medium rounded-lg" data-testid={`start-qc-${r.return_id}`}>
                      <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" /> {notes ? 'Continue QC Inspection' : 'Start QC Inspection'}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(open) => { if (!open) setConfirm(null); }}
        title={confirm ? `Grade ${confirm.ret.return_id} as ${confirmGrade} · ${GRADES[confirmGrade]?.label}?` : ''}
        description={confirm ? `${confirmQty} unit${confirmQty === 1 ? '' : 's'} of ${confirm.ret.sku_code} will move to ${GRADE_ROUTES[confirmGrade]}. Grading can't be undone from the app.` : ''}
        confirmLabel={confirm ? `Grade ${GRADES[confirmGrade]?.label}` : 'Grade'}
        busyLabel="Grading…"
        destructive={confirmGrade === 'C'}
        onConfirm={submitGrade}
        testId="qc-grade-confirm"
      >
        {confirm && notesFor(confirm.ret.return_id).trim() && (
          <p className="rounded-lg bg-gray-50 px-3 py-2 text-[12px] text-gray-700">
            <span className="font-medium">QC notes:</span> {notesFor(confirm.ret.return_id)}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
};

// ═══════════════════════════════════════
// DAMAGE STORE TAB
// ═══════════════════════════════════════
const DamageTab = ({ country, currency, headers }) => {
  const { data, loading, error, reload } = useTabData(`${API}/returns/damage-store?country=${country}`, headers);

  if (loading) return <LoadingState />;
  if (error) return <LoadError what="the damage store" error={error} onRetry={reload} testId="damage-load-error" />;

  return (
    <div className="space-y-4" data-testid="damage-store">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 bg-white rounded-2xl border border-red-200 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">Total Damaged Units</p>
          <p className="text-3xl font-bold tracking-tighter text-gray-900 mt-1">{data?.total_quantity || 0}</p>
        </Card>
        <Card className="p-5 bg-white rounded-2xl border border-red-200 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">Estimated Loss</p>
          <p className="text-3xl font-bold tracking-tighter text-red-700 mt-1">{currency} {(data?.total_estimated_loss || 0).toLocaleString('en-IN')}</p>
        </Card>
      </div>

      {/* Table */}
      <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="damage-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['SKU', 'Product', 'Damaged Qty', 'Damage Reason', 'Est. Loss'].map(h => (
                  <th key={h} className="text-left py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).length > 0 ? data.items.map((item, i) => (
                <tr key={`${item.sku_code}-${item.damage_reason}`} className="border-b border-gray-50 hover:bg-red-50/30 transition-colors" data-testid={`damage-row-${i}`}>
                  <td className="py-2.5 px-4 font-mono text-[11px] text-gray-600">{item.sku_code}</td>
                  <td className="py-2.5 px-4 text-gray-900">{item.product_name}</td>
                  <td className="py-2.5 px-4 font-semibold text-red-700">{item.quantity}</td>
                  <td className="py-2.5 px-4 text-gray-600">{item.damage_reason}</td>
                  <td className="py-2.5 px-4 font-semibold text-red-700">{currency} {(item.estimated_loss || 0).toLocaleString('en-IN')}</td>
                </tr>
              )) : (
                <tr><td colSpan={5} className="py-12 text-center text-gray-500 text-[13px]">No damaged inventory</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════
// SPARE DEPARTMENT TAB
// ═══════════════════════════════════════
const SpareTab = ({ country, currency, headers }) => {
  const { data, loading, error, reload } = useTabData(`${API}/returns/spare-department?country=${country}`, headers);

  if (loading) return <LoadingState />;
  if (error) return <LoadError what="the spare department" error={error} onRetry={reload} testId="spare-load-error" />;

  return (
    <div className="space-y-4" data-testid="spare-department">
      <Card className="p-5 bg-white rounded-2xl border border-amber-200 shadow-sm">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">Total Spare Parts</p>
        <p className="text-3xl font-bold tracking-tighter text-gray-900 mt-1">{data?.total_quantity || 0}</p>
      </Card>

      <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="spare-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['SKU', 'Product', 'Spare Qty', 'Source Returns'].map(h => (
                  <th key={h} className="text-left py-3 px-4 font-semibold text-gray-600 uppercase tracking-wider text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).length > 0 ? data.items.map((item, i) => (
                <tr key={`${item.sku_code}-${item.product_name}`} className="border-b border-gray-50 hover:bg-amber-50/30 transition-colors" data-testid={`spare-row-${i}`}>
                  <td className="py-2.5 px-4 font-mono text-[11px] text-gray-600">{item.sku_code}</td>
                  <td className="py-2.5 px-4 text-gray-900">{item.product_name}</td>
                  <td className="py-2.5 px-4 font-semibold text-amber-700">{item.quantity}</td>
                  <td className="py-2.5 px-4 text-[11px] text-gray-600 font-mono">{(item.source_return_ids || []).length} returns</td>
                </tr>
              )) : (
                <tr><td colSpan={4} className="py-12 text-center text-gray-500 text-[13px]">No spare parts inventory</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════
const LoadingState = () => (
  <div className="flex items-center justify-center h-48" role="status" aria-label="Loading">
    <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
  </div>
);

/** GET for a read-only tab, with error state and a retry. */
const useTabData = (url, headers) => {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let alive = true;
    setState(prev => ({ ...prev, loading: true, error: null }));
    axios.get(url, { headers })
      .then(res => { if (alive) setState({ data: res.data, loading: false, error: null }); })
      .catch(err => { if (alive) setState({ data: null, loading: false, error: err }); });
    return () => { alive = false; };
  }, [url, headers, reloadKey]);
  return { ...state, reload: () => setReloadKey(k => k + 1) };
};

const EmptyChart = () => (
  <div className="h-[220px] flex items-center justify-center text-gray-500 text-[13px]">No data available</div>
);

const GRADES = { A: { label: 'Sellable' }, B: { label: 'Repairable' }, C: { label: 'Damaged' } };
const GRADE_ROUTES = { A: 'Inventory', B: 'Spare Dept', C: 'Damage Store' };
